import {
  Add,
  Close,
  Save,
  ExpandMore,
  UnfoldLess,
  UnfoldMore,
  ExpandLess
} from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Box,
  ButtonGroup,
  Button,
  Divider,
  FormControl,
  Theme,
  FormLabel,
  RadioGroup,
  Radio,
  IconButton
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from 'react-query';
import { Form, Formik, Field, FieldArray } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import React, { FC, useState } from 'react';
import {
  getElevationOptions,
  getStructuralOptions,
  getFramingLevels,
  getParkingOptions,
  getPlanElevation,
  createPlanElevation,
  putPlanElevations
} from '../../../../fetch';
import { IDropdownResponse, IPlanElevations } from '../../../../models';
import { Loader, SMBSelect, Card } from '../../../../components';

interface IPlanElevationDetails {
  elevations: IPlanElevations[];
  isLoadingElevations: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchElevations: () => void;
}

const elevationSchema = Yup.object().shape({
  planElevationId: Yup.number(),
  grade: Yup.string(),
  parking: Yup.string().required('This is required'),
  framingLevel: Yup.string(),
  roofSquareFootage: Yup.number(),
  structuralOptions: Yup.array(),
  cousins: Yup.array(),
  siblings: Yup.array()
});

const formSchema = Yup.object().shape({
  elevation: Yup.array().of(elevationSchema)
});

export const ElevationDetails: FC<IPlanElevationDetails> = ({
  elevations,
  isLoadingElevations,
  refetchElevations,
  setDirtyForm
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { planId }: { planId: string } = useParams();

  const [isSaving, setIsSaving] = useState(false);
  const [selectedElevationOption, setSelectedElevationOption] = useState<string>('');
  const [expanded, setExpanded] = useState([]);

  const handleAccordionChange = panel => {
    if (expanded.includes(panel)) {
      setExpanded(expanded.filter(p => p !== panel));
    } else {
      setExpanded([...expanded, panel]);
    }
  };

  const handleExpandAll = () => {
    if (expanded.length === elevations.length) {
      return setExpanded([]);
    } else {
      const allElevations = elevations.map((elev, i) => {
        return `elevations.${i}`;
      });
      setExpanded(allElevations);
    }
  };

  const {
    isLoading: isLoadingElevationOptions,
    data: elevationOptions,
    refetch: refetchElevationOptions
  } = useQuery<IDropdownResponse[], Error>(['getElevationOptions', planId], () =>
    getElevationOptions(planId)
  );

  const { isLoading: isLoadingStructuralOptions, data: structuralOptions } = useQuery<
    IDropdownResponse[],
    Error
  >(['getStructuralOptions'], () => getStructuralOptions());

  const { isLoading: isLoadingFramingLevels, data: framingLevels } = useQuery<
    IDropdownResponse[],
    Error
  >(['getFramingLevels'], () => getFramingLevels());

  const { isLoading: isLoadingParkingOptions, data: parkingOptions } = useQuery<
    IDropdownResponse[],
    Error
  >(['getParkingOptions'], () => getParkingOptions());

  const initialElevationValues = elevations?.map(obj => {
    const elev: IPlanElevations = {};
    elev.planElevationId = obj?.planElevationId;
    elev.facadeType = obj?.facadeType ?? '';
    elev.grade = obj?.grade ?? '';
    elev.framingLevel = obj?.framingLevel ?? '';
    elev.parking = obj?.parking ?? '';
    elev.structuralOptions = obj?.structuralOptions ?? [];
    elev.cousins = obj?.cousins ?? [];
    elev.siblings = obj?.siblings ?? [];
    elev.roofSquareFootage = obj?.roofSquareFootage ?? 0;

    return elev;
  });

  const ariaExpandedValue = Array.isArray(expanded) ? expanded.length > 0 : expanded;

  return (
    <>
      <Box mt={1}>
        <Grid container alignItems="center" spacing={1}>
          <Grid xs={12} sm={4} item>
            <SMBSelect
              disabled={isSaving || isLoadingElevationOptions || elevationOptions.length === 0}
              name="elevationOption"
              label="Facade"
              variant="outlined"
              handleClear={() => setSelectedElevationOption('')}
              id="elevationOption"
              value={selectedElevationOption}
              onChange={({ target: { value } }) => setSelectedElevationOption(value)}
            >
              {elevationOptions &&
                elevationOptions.map((elevation, index) => {
                  return (
                    <MenuItem key={`${index}`} value={elevation.value}>
                      {elevation.value}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid xs={12} sm={4} item>
            <Button
              id="save"
              color="primary"
              variant="contained"
              disabled={!selectedElevationOption}
              startIcon={<Add />}
              onClick={async () => {
                try {
                  setIsSaving(true);
                  const elevationID = await createPlanElevation(planId, selectedElevationOption);
                  const newElevation = await getPlanElevation(elevationID);
                  elevations = [...elevations, newElevation];

                  refetchElevations();
                  refetchElevationOptions();
                  setSelectedElevationOption('');
                } catch (error) {
                  enqueueSnackbar(error?.Detail ?? `Error adding facade, please try again.`, {
                    variant: 'error'
                  });
                  console.log(error);
                } finally {
                  setIsSaving(false);
                }
              }}
            >
              Add
            </Button>
          </Grid>
        </Grid>
        {!isLoadingElevations && elevations?.length > 0 && (
          <Box display="flex" justifyContent="flex-end">
            <IconButton
              onClick={() => handleExpandAll()}
              title={`${expanded.length > 0 ? 'Collapse' : 'Expand'} all elevations`}
            >
              {expanded.length === elevations.length ? <UnfoldLess /> : <UnfoldMore />}
            </IconButton>
          </Box>
        )}
      </Box>
      <Box>
        {isLoadingElevations && (
          <Box
            mt={1}
            minHeight="30rem"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Loader type="inline" position="centered">
              Loading...
            </Loader>
          </Box>
        )}
        {!isLoadingElevations && (
          <Formik
            enableReinitialize={true}
            initialValues={{
              elevations: initialElevationValues
            }}
            validationSchema={formSchema}
            onSubmit={async (values, actions) => {
              try {
                const data: any = {
                  ...values
                };

                await putPlanElevations(data);
                refetchElevations();
                enqueueSnackbar('Elevation(s) has been updated successfully!', {
                  variant: 'success'
                });
              } catch (error) {
                enqueueSnackbar(
                  error?.Detail ?? 'Something went wrong. Elevation(s) was not updated.',
                  {
                    variant: 'error'
                  }
                );
              }
            }}
          >
            {({
              isSubmitting,
              values,
              setFieldValue,
              handleSubmit,
              dirty,
              isValid,
              handleBlur
            }) => {
              if (dirty) {
                setTimeout(() => setDirtyForm(true), 0);
              } else {
                setTimeout(() => setDirtyForm(false), 0);
              }

              return (
                <>
                  {isSubmitting && (
                    <Loader type="fullscreen" position="centered">
                      Saving...
                    </Loader>
                  )}

                  {isSaving && (
                    <Loader type="overlay" position="centered">
                      Saving...
                    </Loader>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Box mt={1}>
                      <FieldArray
                        name="elevations"
                        render={arrayHelpers => (
                          <div className={classes.wrapper}>
                            {values.elevations.map((elevation, i) => (
                              <>
                                <Card
                                  title={
                                    <>
                                      <Typography>{elevation.facadeType}</Typography>
                                    </>
                                  }
                                  isExpanded={expanded.includes(`elevations.${i}`)}
                                  action={
                                    <IconButton
                                      onClick={() => handleAccordionChange(`elevations.${i}`)}
                                      aria-expanded={ariaExpandedValue}
                                      aria-label="show more"
                                    >
                                      {expanded.includes(`elevations.${i}`) ? (
                                        <ExpandLess />
                                      ) : (
                                        <ExpandMore />
                                      )}
                                    </IconButton>
                                  }
                                  cardHeaderSx={{
                                    padding: '14px'
                                  }}
                                >
                                  <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                      <Box mt={1}>
                                        <Grid item xs={12}>
                                          <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={6}>
                                              <Field name={`elevations.${i}.parking`}>
                                                {({ field }) => {
                                                  return (
                                                    <SMBSelect
                                                      required
                                                      name={field.name}
                                                      label="Parking"
                                                      variant="outlined"
                                                      id="parking"
                                                      disabled={isLoadingParkingOptions}
                                                      value={field.value}
                                                      onChange={e => {
                                                        setFieldValue(
                                                          `elevations.${i}.parking`,
                                                          e.target.value
                                                        );
                                                      }}
                                                    >
                                                      {parkingOptions?.map((parking, index) => {
                                                        return (
                                                          <MenuItem
                                                            key={`elevations.${index}.parking`}
                                                            value={parking.value}
                                                          >
                                                            {parking.description}
                                                          </MenuItem>
                                                        );
                                                      })}
                                                    </SMBSelect>
                                                  );
                                                }}
                                              </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                              <Field name={`elevations.${i}.framingLevel`}>
                                                {({ field }) => {
                                                  return (
                                                    <FormControl
                                                      fullWidth
                                                      variant="outlined"
                                                      size="small"
                                                    >
                                                      <SMBSelect
                                                        name={`elevations.${i}.framingLevel`}
                                                        label="Framing Level"
                                                        id="framingLevel"
                                                        variant="outlined"
                                                        disabled={isLoadingFramingLevels}
                                                        value={field.value}
                                                        onChange={e => {
                                                          setFieldValue(
                                                            `elevations.${i}.framingLevel`,
                                                            e.target.value
                                                          );
                                                        }}
                                                      >
                                                        {framingLevels?.map((frame, index) => {
                                                          return (
                                                            <MenuItem
                                                              key={`elevations.${index}.framingLevel`}
                                                              value={frame.value}
                                                            >
                                                              {frame.description}
                                                            </MenuItem>
                                                          );
                                                        })}
                                                      </SMBSelect>
                                                    </FormControl>
                                                  );
                                                }}
                                              </Field>
                                            </Grid>
                                            {/* <Grid item xs={12} sm={6}>
                                              <Field name={`elevations.${i}.siblings`}>
                                                {({ field }) => {
                                                  return (
                                                    <FormControl
                                                      component="fieldset"
                                                      variant="standard"
                                                    >
                                                      <FormLabel component="legend">
                                                        Siblings
                                                      </FormLabel>
                                                      <FormGroup
                                                        sx={{
                                                          position: 'flex',
                                                          flexDirection: 'row',
                                                          justifyContent: 'left',
                                                          alignItems: 'start'
                                                        }}
                                                      >
                                                        {siblingsAndCousinsOptions?.map(
                                                          (sibling, index) => {
                                                            return (
                                                              <FormControlLabel
                                                                key={`elevations.${index}.value`}
                                                                control={
                                                                  <Checkbox
                                                                    checked={values.elevations[
                                                                      `${i}`
                                                                    ].siblings.includes(
                                                                      sibling.value
                                                                    )}
                                                                    onChange={() => {
                                                                      values.elevations[
                                                                        `${i}`
                                                                      ].siblings.includes(
                                                                        sibling.value
                                                                      )
                                                                        ? setFieldValue(
                                                                            `elevations.${i}.siblings`,
                                                                            values.elevations[
                                                                              `${i}`
                                                                            ].siblings.filter(
                                                                              val =>
                                                                                val !==
                                                                                sibling.value
                                                                            )
                                                                          )
                                                                        : setFieldValue(
                                                                            `elevations.${i}.siblings`,
                                                                            [
                                                                              ...values.elevations[
                                                                                `${i}`
                                                                              ].siblings,
                                                                              sibling.value
                                                                            ]
                                                                          );
                                                                    }}
                                                                    color="primary"
                                                                  />
                                                                }
                                                                label={sibling.description}
                                                              />
                                                            );
                                                          }
                                                        )}
                                                      </FormGroup>
                                                    </FormControl>
                                                  );
                                                }}
                                              </Field>
                                            </Grid> */}
                                          </Grid>
                                        </Grid>
                                      </Box>
                                      <Box>
                                        <Grid item xs={12}>
                                          <Grid container alignItems="center" spacing={1}>
                                            {/* <Grid item xs={12} sm={6}>
                                              <Field name={`elevations.${i}.cousins`}>
                                                {({ field }) => {
                                                  return (
                                                    <FormControl
                                                      component="fieldset"
                                                      variant="standard"
                                                    >
                                                      <FormLabel component="legend">
                                                        Cousins
                                                      </FormLabel>
                                                      <FormGroup
                                                        sx={{
                                                          position: 'flex',
                                                          flexDirection: 'row',
                                                          justifyContent: 'left',
                                                          alignItems: 'start'
                                                        }}
                                                      >
                                                        {siblingsAndCousinsOptions?.map(
                                                          (cousin, index) => {
                                                            return (
                                                              <FormControlLabel
                                                                key={`elevations.${index}.value`}
                                                                control={
                                                                  <Checkbox
                                                                    checked={values.elevations[
                                                                      `${i}`
                                                                    ].cousins.includes(
                                                                      cousin.value
                                                                    )}
                                                                    onChange={() => {
                                                                      values.elevations[
                                                                        `${i}`
                                                                      ].cousins.includes(
                                                                        cousin.value
                                                                      )
                                                                        ? setFieldValue(
                                                                            `elevations.${i}.cousins`,
                                                                            values.elevations[
                                                                              `${i}`
                                                                            ].cousins.filter(
                                                                              val =>
                                                                                val !== cousin.value
                                                                            )
                                                                          )
                                                                        : setFieldValue(
                                                                            `elevations.${i}.cousins`,
                                                                            [
                                                                              ...values.elevations[
                                                                                `${i}`
                                                                              ].cousins,
                                                                              cousin.value
                                                                            ]
                                                                          );
                                                                    }}
                                                                    color="primary"
                                                                  />
                                                                }
                                                                label={cousin.description}
                                                              />
                                                            );
                                                          }
                                                        )}
                                                      </FormGroup>
                                                    </FormControl>
                                                  );
                                                }}
                                              </Field>
                                            </Grid> */}
                                          </Grid>
                                        </Grid>
                                      </Box>
                                      <Box mt={2}>
                                        <Grid item xs={12}>
                                          <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={6}>
                                              <Field name={`elevations.${i}.roofSquareFootage`}>
                                                {({ field }) => {
                                                  return (
                                                    <TextField
                                                      fullWidth
                                                      type="number"
                                                      variant="outlined"
                                                      autoComplete="nope"
                                                      label="Roof Square Footage"
                                                      name={field.name}
                                                      size="small"
                                                      value={field.value}
                                                      onBlur={handleBlur}
                                                      onChange={e =>
                                                        setFieldValue(
                                                          `elevations[${i}].roofSquareFootage`,
                                                          Number(e.target.value)
                                                        )
                                                      }
                                                    />
                                                  );
                                                }}
                                              </Field>
                                            </Grid>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                      <Box mt={1}>
                                        <Grid item xs={12}>
                                          <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={4}>
                                              <Field name={`elevations.${i}.grade`}>
                                                {({ field }) => {
                                                  return (
                                                    <Box padding="0.5rem">
                                                      <FormControl
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                      >
                                                        <Typography>Grade</Typography>
                                                        <RadioGroup
                                                          value={field.value}
                                                          onChange={e =>
                                                            setFieldValue(
                                                              `elevations.${i}.grade`,
                                                              e.target.value
                                                            )
                                                          }
                                                          aria-label="grade"
                                                          name={field.name}
                                                        >
                                                          <FormControlLabel
                                                            value="Base"
                                                            control={<Radio />}
                                                            label="Base"
                                                          />
                                                          <FormControlLabel
                                                            value="HighEnd"
                                                            control={<Radio />}
                                                            label="High End"
                                                          />
                                                        </RadioGroup>
                                                      </FormControl>
                                                    </Box>
                                                  );
                                                }}
                                              </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                              <Field name={`elevations.${i}.structuralOptions`}>
                                                {({ field }) => {
                                                  return (
                                                    <FormControl
                                                      component="fieldset"
                                                      variant="standard"
                                                    >
                                                      <FormLabel component="legend">
                                                        Structural Options
                                                      </FormLabel>
                                                      <FormGroup>
                                                        {!isLoadingStructuralOptions &&
                                                          structuralOptions?.map(
                                                            (struct, index) => {
                                                              return (
                                                                <FormControlLabel
                                                                  key={`elevations.${index}.value`}
                                                                  control={
                                                                    <Checkbox
                                                                      checked={values.elevations[
                                                                        `${i}`
                                                                      ].structuralOptions.includes(
                                                                        struct.value
                                                                      )}
                                                                      onChange={() => {
                                                                        values.elevations[
                                                                          `${i}`
                                                                        ].structuralOptions.includes(
                                                                          struct.value
                                                                        )
                                                                          ? setFieldValue(
                                                                              `elevations.${i}.structuralOptions`,
                                                                              values.elevations[
                                                                                `${i}`
                                                                              ].structuralOptions.filter(
                                                                                val =>
                                                                                  val !==
                                                                                  struct.value
                                                                              )
                                                                            )
                                                                          : setFieldValue(
                                                                              `elevations.${i}.structuralOptions`,
                                                                              [
                                                                                ...values
                                                                                  .elevations[
                                                                                  `${i}`
                                                                                ].structuralOptions,
                                                                                struct.value
                                                                              ]
                                                                            );
                                                                      }}
                                                                      color="primary"
                                                                    />
                                                                  }
                                                                  label={struct.description}
                                                                />
                                                              );
                                                            }
                                                          )}
                                                      </FormGroup>
                                                    </FormControl>
                                                  );
                                                }}
                                              </Field>
                                            </Grid>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Card>
                              </>
                            ))}
                          </div>
                        )}
                      />
                    </Box>
                    <Box mt={1} mb={0.5}>
                      <Divider />
                    </Box>
                    <ButtonGroup>
                      <Button
                        disabled={!dirty || isSubmitting || !isValid}
                        type="submit"
                        startIcon={planId ? <Save /> : <Add />}
                        variant="contained"
                        color="primary"
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        color="inherit"
                        startIcon={<Close />}
                        onClick={() => {
                          history.push('/development/plans/manage-plans');
                        }}
                      >
                        Cancel
                      </Button>
                    </ButtonGroup>
                  </Form>
                </>
              );
            }}
          </Formik>
        )}
      </Box>
    </>
  );
};

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  wrapper: {
    '&& .MuiPaper-elevation1': {
      boxShadow: 'none',
      margin: 0,
      borderTop: `1px solid ${theme.palette.grey[300]}`
    }
  },
  panelRoot: {
    margin: theme.spacing(1, 0)
  },
  requestTitle: {
    minHeight: '100%',
    '&& .MuiExpansionPanelSummary-content .Mui-expanded': {
      margin: '0'
    }
  },
  panelSummaryRoot: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    borderLeft: `3px solid #006f3c`,
    paddingLeft: `10px`,
    paddingRight: '10px',
    minHeight: '100% !important',
    '&& .MuiExpansionPanelSummary-content.Mui-expanded': {
      margin: '12px 0'
    }
  }
}));
